import { useEffect, useState } from "react";
import { Trophy, Scale, Utensils } from "lucide-react";
import { api } from "@/lib/api";
import { DashboardShell } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const medal = ["text-yellow-500", "text-stone-400", "text-orange-700"];

export default function Leaderboard() {
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/leaderboard?period=${period}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [period]);

  const top = rows.slice(0, 3);

  return (
    <DashboardShell title="Donation leaderboard" subtitle="Celebrating the stores rescuing the most food.">
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="bg-stone-100 rounded-full p-1">
          <TabsTrigger data-testid="lb-all" value="all" className="rounded-full data-[state=active]:bg-white">All-time</TabsTrigger>
          <TabsTrigger data-testid="lb-month" value="month" className="rounded-full data-[state=active]:bg-white">This month</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Podium */}
      {top.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {top.map((r, i) => (
            <div key={r.rank} className={`rounded-3xl p-8 border shadow-sm ${i === 0 ? "bg-emerald-900 text-emerald-50 border-emerald-900 md:-translate-y-2" : "bg-white border-stone-200"}`}>
              <div className="flex items-center justify-between">
                <Trophy className={`w-8 h-8 ${i === 0 ? "text-yellow-300" : medal[i]}`} />
                <span className={`font-heading text-4xl font-semibold ${i === 0 ? "text-emerald-50" : "text-stone-300"}`}>#{r.rank}</span>
              </div>
              <p className={`font-heading text-xl font-medium mt-5 ${i === 0 ? "text-white" : "text-stone-900"}`}>{r.store_name}</p>
              <p className={`text-sm ${i === 0 ? "text-emerald-200/80" : "text-stone-500"}`}>{r.city}</p>
              <p className={`font-heading text-3xl font-semibold mt-4 ${i === 0 ? "text-white" : "text-emerald-800"}`}>{r.lbs.toLocaleString()} lbs</p>
              <p className={`text-sm ${i === 0 ? "text-emerald-200/80" : "text-stone-500"}`}>≈ {r.meals.toLocaleString()} meals</p>
            </div>
          ))}
        </div>
      )}

      {/* Full ranking */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm divide-y divide-stone-100" data-testid="leaderboard-list">
        {rows.length === 0 && <p className="p-8 text-center text-stone-500">No completed donations yet. Be the first on the board!</p>}
        {rows.map((r) => (
          <div key={r.rank} data-testid={`lb-row-${r.rank}`} className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="font-heading text-lg font-semibold text-stone-300 w-8 text-center">{r.rank}</span>
              <div>
                <p className="font-medium text-stone-900">{r.store_name}</p>
                <p className="text-sm text-stone-500">{r.city} · {r.count} pickups</p>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <span className="flex items-center gap-2 text-stone-700"><Scale className="w-4 h-4 text-stone-400" /> {r.lbs.toLocaleString()} lbs</span>
              <span className="flex items-center gap-2 text-emerald-800 font-medium"><Utensils className="w-4 h-4" /> {r.meals.toLocaleString()} meals</span>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
